import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { TH, TD } from './adminStyles';
import { billingService } from '../../services/billingService';
import { metricsService } from '../../services/metricsService';

/**
 * Оплаты и доля офисов: запись оплаты по счёту и расчёт доли офиса-продавца.
 *
 * Доля — 50 % с первой оплаты клиента и 25 % с каждой следующей, картой или по
 * счёту (решение владельца 15–16.09.2026). Считает bit-metrics
 * (`office_share.py`), здесь только показ. Оплата по счёту — деньги по выписке
 * банка; тариф она не выдаёт, доступ выдаётся заявкой на счёт или вручную.
 * Спека: docs/superpowers/specs/2026-09-28-office-share-and-invoice-payments-design.md.
 */
const REGISTRY_LABEL = {
  off: 'не отправлена',
  sent: 'в реестре',
  confirmed: 'подтверждена реестром',
  failed: 'ошибка отправки',
  duplicate: 'дубль в реестре',
};
// Плашки статуса — те же, что у заявок на счёт (BillingTab).
const REGISTRY_BADGE = {
  off: 'bg-paper-bg text-paper-ink-2',
  sent: 'bg-paper-panel text-paper-accent',
  confirmed: 'bg-emerald-100 text-emerald-700',
  failed: 'bg-paper-accent/[0.08] text-paper-accent',
  duplicate: 'bg-paper-panel text-paper-warning',
};

const METHOD_LABEL = { bank_transfer: 'по счёту', card: 'картой' };

const rub = (v) => `${Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽`;
const day = (v) => (v ? new Date(v).toLocaleDateString('ru-RU') : '—');
const thisMonth = () => new Date().toISOString().slice(0, 7);
const errorText = (e, fallback) => {
  const d = e && e.response && e.response.data && e.response.data.detail;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  return d || fallback;
};

const EMPTY = {
  email: '', amount_rub: '', payer_inn: '', payer_kpp: '', payer_name: '',
  invoice_number: '', paid_on: '', bank_doc_number: '', service_period_from: '',
  service_period_to: '', seller_office: '', channel: 'office', comment: '',
};

export default function PaymentsTab() {
  const [month, setMonth] = useState(thisMonth());
  const [share, setShare] = useState(null);
  const [payments, setPayments] = useState([]);
  const [offices, setOffices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(null);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);

  const loadShare = useCallback(async (m) => {
    try {
      setShare(await metricsService.getOfficeShare(m));
    } catch (e) {
      setShare(null);
      setError(errorText(e, 'Не удалось посчитать долю офисов'));
    }
  }, []);

  const loadPayments = useCallback(async () => {
    try {
      const data = await billingService.adminGetPayments({ method: 'bank_transfer', page_size: 100 });
      setPayments(data.items || []);
    } catch (e) {
      setError(errorText(e, 'Не удалось загрузить оплаты по счёту'));
    }
  }, []);

  useEffect(() => { loadShare(month); }, [month, loadShare]);
  useEffect(() => {
    loadPayments();
    metricsService.getOfficeMap()
      .then((d) => { setOffices((d.offices || []).map((o) => o.office)); setAccounts(d.accounts || []); })
      .catch(() => {});
  }, [loadPayments]);

  const run = async (fn, ok) => {
    setError(null); setNote(null);
    try {
      setNote(ok(await fn()));
      await Promise.all([loadPayments(), loadShare(month)]);
      return true;
    } catch (e) {
      setError(errorText(e, 'Не удалось выполнить действие'));
      return false;
    }
  };

  // Офис-продавец по умолчанию — закрепление аккаунта-плательщика; админ правит.
  const setEmail = (email) => {
    const acc = accounts.find((a) => a.account === email.trim().toLowerCase());
    setForm((f) => ({ ...f, email, seller_office: f.seller_office || (acc && acc.office) || '' }));
  };

  const submit = async () => {
    const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ''));
    body.amount_rub = Number(String(form.amount_rub).replace(',', '.').replace(/\s/g, ''));
    const ok = await run(() => billingService.adminRecordOfflinePayment(body),
      (p) => `Записана оплата ${rub(p.amount_rub)} от ${p.payer_name}`
        + (p.registry && p.registry.state !== 'off' ? `, реестр: ${REGISTRY_LABEL[p.registry.state]}` : ''));
    if (ok) setForm(null);
  };

  const input = (key, label, props = {}) => (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-paper-ink-3">{label}</span>
      <Input value={form[key]} aria-label={label}
             onChange={(e) => (key === 'email' ? setEmail(e.target.value)
               : setForm({ ...form, [key]: e.target.value }))}
             {...props} />
    </label>
  );

  return (
    <div data-testid="admin-payments" className="flex flex-col gap-8 text-sm">
      <section>
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-sm font-semibold text-paper-ink">Доля офисов</h3>
          <span className="text-xs text-paper-hint">50 % с первой оплаты клиента, 25 % со следующих</span>
          <div className="flex-1" />
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                 aria-label="Месяц" className="px-3 py-2 rounded-chip bg-paper-bg text-sm" />
        </div>

        {share && share.unassigned_share_rub > 0 && (
          <div className="bg-paper-accent/[0.06] text-paper-accent rounded-panel p-3 mb-3">
            {rub(share.unassigned_share_rub)} без офиса: закрепите плательщика за офисом во вкладке
            «Пользователи» или укажите офис в оплате по счёту.
          </div>
        )}

        {share && (share.by_office.length === 0 ? (
          <div className="text-center py-8 text-paper-hint">За этот месяц оплат с долей офиса нет</div>
        ) : (
          <Card className="overflow-hidden">
            <table data-testid="office-share" className="w-full text-sm">
              <thead>
                <tr className="bg-paper-panel border-b text-left">
                  <th className={TH}>Офис</th><th className={TH}>Оплат</th><th className={TH}>Из них первых</th>
                  <th className={`${TH} text-right`}>Сумма</th><th className={`${TH} text-right`}>Доля офиса</th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {share.by_office.map((o) => (
                  <tr key={o.office} className="border-b">
                    <td className={TD}>{o.office}</td>
                    <td className={`${TD} text-paper-ink-3`}>{o.payments}</td>
                    <td className={`${TD} text-paper-ink-3`}>{o.first}</td>
                    <td className={`${TD} text-right text-paper-ink-3`}>{rub(o.net_rub)}</td>
                    <td className={`${TD} text-right font-semibold`}>{rub(o.share_rub)}</td>
                  </tr>
                ))}
                <tr className="bg-paper-panel">
                  <td className={`${TD} font-semibold`} colSpan={4}>Итого к выплате офисам</td>
                  <td className={`${TD} text-right font-semibold`}>{rub(share.total_share_rub)}</td>
                </tr>
              </tbody>
            </table>
          </Card>
        ))}

        {share && share.rows.length > 0 && (
          <details className="mt-3">
            <summary className="cursor-pointer text-xs text-paper-hint hover:text-paper-ink-2">
              Оплаты месяца: {share.rows.length}
            </summary>
            <Card className="overflow-hidden mt-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-paper-panel border-b text-left">
                    <th className={TH}>Дата</th><th className={TH}>Плательщик</th><th className={TH}>Способ</th>
                    <th className={TH}>Офис</th><th className={TH}>Оплата</th>
                    <th className={`${TH} text-right`}>Сумма</th><th className={`${TH} text-right`}>Доля</th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {share.rows.map((r) => (
                    <tr key={r.order_ref} className={`border-b ${r.rate === 0 ? 'opacity-50' : ''}`}>
                      <td className={`${TD} text-paper-ink-3 whitespace-nowrap`}>{day(r.paid_at)}</td>
                      <td className={TD}>{r.payer_name || r.email}</td>
                      <td className={`${TD} text-paper-ink-3`}>{METHOD_LABEL[r.method] || r.method}</td>
                      <td className={`${TD} text-paper-ink-3`}>{r.office || '—'}</td>
                      <td className={`${TD} text-paper-ink-3`}>{r.note || (r.seq === 1 ? 'первая' : `${r.seq}-я`)}</td>
                      <td className={`${TD} text-right`}>{rub(r.net_rub)}</td>
                      <td className={`${TD} text-right`}>{rub(r.share_rub)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </details>
        )}
      </section>

      <section>
        <div className="flex items-center gap-3 mb-3">
          <h3 className="text-sm font-semibold text-paper-ink">Оплаты по счёту</h3>
          <span className="text-xs text-paper-hint">деньги по выписке банка; тариф выдаётся отдельно</span>
          <div className="flex-1" />
          {!form && (
            <Button size="sm" className="bg-paper-accent hover:bg-paper-accent-hover" onClick={() => setForm(EMPTY)}>
              <Plus className="w-4 h-4 mr-1" /> Зафиксировать оплату по счёту
            </Button>
          )}
        </div>

        {form && (
          <Card data-testid="offline-form" className="p-4 mb-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {input('email', 'Плательщик в Ньютоне (почта владельца)', { list: 'payer-accounts' })}
              {input('amount_rub', 'Сумма, ₽', { inputMode: 'decimal' })}
              {input('paid_on', 'Дата поступления', { type: 'date' })}
              {input('payer_name', 'Плательщик по выписке')}
              {input('payer_inn', 'ИНН', { inputMode: 'numeric' })}
              {input('payer_kpp', 'КПП')}
              {input('invoice_number', 'Номер счёта')}
              {input('bank_doc_number', 'Номер платёжки')}
              {input('seller_office', 'Офис-продавец', { list: 'seller-offices' })}
              {input('service_period_from', 'Оплачено с', { type: 'date' })}
              {input('service_period_to', 'Оплачено по', { type: 'date' })}
              <label className="flex flex-col gap-1">
                <span className="text-xs text-paper-ink-3">Канал</span>
                <select value={form.channel} aria-label="Канал"
                        onChange={(e) => setForm({ ...form, channel: e.target.value })}
                        className="px-3 py-2 rounded-chip bg-paper-bg text-sm">
                  <option value="office">офис — доля по правилу 50/25</option>
                  <option value="partner">партнёр — без доли</option>
                </select>
              </label>
            </div>
            <div className="mt-3">{input('comment', 'Комментарий')}</div>
            <datalist id="seller-offices">{offices.map((o) => <option key={o} value={o} />)}</datalist>
            <datalist id="payer-accounts">{accounts.map((a) => <option key={a.account} value={a.account}>{a.name}</option>)}</datalist>
            <div className="mt-4 flex gap-2">
              <Button size="sm" className="bg-paper-accent hover:bg-paper-accent-hover" onClick={submit}>Записать</Button>
              <Button size="sm" variant="outline" onClick={() => setForm(null)}>Отмена</Button>
            </div>
          </Card>
        )}

        {payments.length === 0 ? (
          <div className="text-center py-8 text-paper-hint">Оплат по счёту пока нет</div>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table data-testid="offline-payments" className="w-full text-sm">
                <thead>
                  <tr className="bg-paper-panel border-b text-left">
                    <th className={TH}>Поступила</th><th className={TH}>Плательщик</th><th className={TH}>Счёт</th>
                    <th className={TH}>Офис</th><th className={`${TH} text-right`}>Сумма</th>
                    <th className={TH}>Реестр</th><th className={TH}> </th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {payments.map((p) => {
                    const state = (p.registry && p.registry.state) || 'off';
                    return (
                      <tr key={p.order_ref} className={`border-b ${p.reversed_at ? 'opacity-50' : ''}`}>
                        <td className={`${TD} text-paper-ink-3 whitespace-nowrap`}>{day(p.paid_at)}</td>
                        <td className={TD}>
                          {p.payer_name}
                          <div className="text-xs text-paper-hint">{p.email}</div>
                        </td>
                        <td className={`${TD} text-paper-ink-3`}>{p.invoice_number}</td>
                        <td className={`${TD} text-paper-ink-3`}>{p.channel === 'partner' ? 'партнёр' : (p.seller_office || '—')}</td>
                        <td className={`${TD} text-right whitespace-nowrap`}>{rub(p.amount_rub)}</td>
                        <td className={TD}>
                          <span className={`inline-block text-xs rounded-full px-2 py-0.5 ${
                            p.reversed_at ? 'bg-paper-track text-paper-ink-3' : (REGISTRY_BADGE[state] || REGISTRY_BADGE.off)}`}>
                            {p.reversed_at ? 'сторно' : REGISTRY_LABEL[state] || state}
                          </span>
                        </td>
                        <td className={TD}>
                          <div className="flex justify-end gap-1">
                            {!p.reversed_at && ['off', 'failed'].includes(state) && (
                              <Button variant="outline" size="sm"
                                      onClick={() => run(() => billingService.adminPushOfflinePayment(p.order_ref),
                                        (r) => `Реестр: ${REGISTRY_LABEL[r.registry.state] || r.registry.state}`)}>
                                В реестр
                              </Button>
                            )}
                            {!p.reversed_at && (
                              <Button variant="outline" size="sm" className="text-paper-accent"
                                      aria-label={`Сторно ${p.invoice_number}`}
                                      onClick={() => {
                                        const reason = window.prompt('Причина сторно (запись останется в истории):');
                                        if (!reason) return;
                                        run(() => billingService.adminReverseOfflinePayment(p.order_ref, reason),
                                            () => 'Сторно проведено');
                                      }}>
                                Сторно
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      {note && <div data-testid="admin-payments-result" className="bg-paper-panel rounded-panel p-3 text-paper-ink">{note}</div>}
      {error && <div data-testid="admin-payments-error" className="bg-paper-accent/[0.06] text-paper-accent rounded-panel p-3">{error}</div>}
    </div>
  );
}
